import { Brain, ChevronRight, type LucideIcon } from "lucide-react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger, } from "@/components/ui/collapsible"
import { SidebarGroup, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, useSidebar } from "@/components/ui/sidebar"
import { Link, useNavigate } from "react-router-dom"
import { usePermissions } from "@/lib/hooks/usePermissions"

interface Props {
  items: {
    resource?: string
    title: string
    url: string
    icon?: LucideIcon
    isActive?: boolean
    isLink?: ConstrainBoolean
    items?: {
      title: string
      url: string
      onClick?: () => void
      resource?: string
    }[]
  }[]
}

export function NavMain({ items }: Props) {
  const { canRead, loading: permissionsLoading } = usePermissions()
  const { state } = useSidebar()
  const navigate = useNavigate()

  // When the sidebar is collapsed and a parent category is clicked,
  // navigate to the first available child page as a sensible fallback.
  const handleIconClick = (
    event: React.MouseEvent,
    item: Props["items"][number]
  ) => {
    if (state !== "collapsed") return

    const firstChild = item.items?.find((s) => !!s.url && s.resource && canRead(s.resource))
    const targetUrl = firstChild?.url || item.url || ""

    if (targetUrl) {
      event.preventDefault()
      event.stopPropagation()
      navigate(targetUrl)
    }
  }

  return <SidebarGroup>
    <SidebarGroupLabel>Platform</SidebarGroupLabel>

    <SidebarMenu>
      {items.map((item) => {
        if (item.isLink && item.resource && canRead(item.resource)) {
          return (
            <SidebarMenuItem key={item.title}>
              <Link to={item.url} className="w-full">
                <SidebarMenuButton tooltip={item.title}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          )
        }

        const visibleSubItems = item.items?.filter((subItem) => subItem.resource && canRead(subItem.resource)) ?? []
        if (visibleSubItems.length === 0) return null

        return (
          <Collapsible key={item.title} asChild defaultOpen={item.isActive} className="group/collapsible">
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton 
                  tooltip={item.title} 
                  onClick={(e) => handleIconClick(e, item)}
                >
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {visibleSubItems.map((subItem) => (
                    <SidebarMenuSubItem key={subItem.title}>
                      {subItem.onClick ? (
                        <SidebarMenuSubButton onClick={subItem.onClick} title={subItem.title}>
                          <span>{subItem.title}</span>
                        </SidebarMenuSubButton>
                      ) : (
                        <SidebarMenuSubButton asChild title={subItem.title}>
                          <Link to={subItem.url}>
                            <span>{subItem.title}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      )}
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        )
      })}
    </SidebarMenu>
  </SidebarGroup>
}
